import { Dialog, DialogRef, DialogConfig } from '@angular/cdk/dialog';
import { Inject, Injectable, Injector, Type } from '@angular/core';
import { DrawerComponent } from './drawer.component';
import {
  DrawerButton,
  DrawerButtonConfig,
  DrawerComponentInputs,
  DrawerConfig,
  DrawerResult,
  DrawerResultType,
  DrawerState,
} from './drawer.model';
import { Subject } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { ToastService } from '../../services';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class DrawerService {
  private i18n: Record<string, string> = this.translateService.instant([
    'Core_Save',
    'Core_Cancel',
    'Core_Clear',
  ]);

  private _config: DrawerConfig;
  private _drawer: DrawerState;
  private _dialogRef: DialogRef<DrawerResult>;
  private _trackedSubjects: Subject<void>[] = [];
  private _rootEl: Element = this.document.getElementsByTagName('dgx-root')[0];
  private _bodyEl: Element = this.document.getElementsByTagName('body')[0];

  constructor(
    private dialog: Dialog,
    private injector: Injector,
    private toastService: ToastService,
    private translateService: TranslateService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  /** Access drawer buttons, throws if drawer not ready */
  public get buttons() {
    if (!this._drawer) {
      throw new Error(
        'Drawer not setup, buttons not available yet. Call open() first.'
      );
    }
    return this._drawer.buttons;
  }

  /** Access drawer processing state, throws if drawer not ready */
  public get processing() {
    if (!this._drawer) {
      throw new Error(
        'Drawer not setup, processing not available yet. Call open() first.'
      );
    }
    return this._drawer.processing;
  }

  /** Open a new drawer */
  public open<T>(
    contentComponent: Type<T>,
    options: { config: DrawerConfig; inputs?: Partial<T> } = {
      config: { title: '', disableClose: false },
    }
  ): DialogRef<DrawerResult> {
    this.disableBackgroundInteraction();
    this._config = options.config;
    this._drawer = this.setupDrawerState();

    const dialogConfig = this.createDialogConfig(
      contentComponent,
      options.inputs
    );
    this._dialogRef = this.dialog.open(DrawerComponent, dialogConfig);

    this.setupDefaultButtonHandlers();

    this._dialogRef.closed.subscribe((drawerOutput: DrawerResult) => {
      this.handleDrawerClosed(drawerOutput);
    });

    return this._dialogRef;
  }

  /** Close the drawer with a result */
  public close<T>(result: DrawerResultType = 'close', data: T = null) {
    if (!this._dialogRef) {
      throw new Error('Trying to close drawer when no drawer exists.');
    }
    (this._dialogRef.componentRef.instance as DrawerComponent).drawerClose({
      result,
      data,
    });
  }

  /** Set drawer into a processing state */
  public setProcessing() {
    this.buttons.secondaryButton.visible = false;
    this.buttons.tertiaryButton.visible = false;
    this._drawer.disableClose = true;
    this._drawer.processing = true;
  }

  /** Build the DialogConfig object */
  private createDialogConfig<T>(
    contentComponent: Type<T>,
    inputs?: Partial<T>
  ): DialogConfig {
    return {
      disableClose: true,
      width: 'auto',
      ariaLabelledBy: 'drawer-title',
      injector: this.injector,
      data: {
        drawer: this._drawer,
        contentComponent,
        inputs: inputs || {},
      } satisfies DrawerComponentInputs,
    };
  }

  /** Setup drawer model based on current config */
  private setupDrawerState(): DrawerState {
    return {
      title: this._config.title,
      disableClose: this._config.disableClose,
      processing: false,
      buttons: {
        primaryButton: this.setupButton(
          this._config.buttons?.primaryButton,
          this.i18n.Core_Save
        ),
        secondaryButton: this.setupButton(
          this._config.buttons?.secondaryButton,
          this.i18n.Core_Cancel
        ),
        tertiaryButton: this.setupButton(
          this._config.buttons?.tertiaryButton,
          this.i18n.Core_Clear,
          false
        ),
      },
    };
  }

  /** Setup a button and its click tracking */
  private setupButton(
    button: DrawerButtonConfig,
    defaultLabel: string,
    visible: boolean = true
  ): DrawerButton | undefined {
    const clicked$ = new Subject<void>();
    this._trackedSubjects.push(clicked$);

    return {
      label: button?.label ?? defaultLabel,
      visible: button?.visible ?? visible,
      clicked$: clicked$,
    };
  }

  /** Attach default handlers for buttons if none were supplied */
  private setupDefaultButtonHandlers(): void {
    if (!this._config?.buttons?.primaryButton) {
      this.attachDefaultButtonHandler(
        this._drawer.buttons.primaryButton,
        'complete'
      );
    }
    if (!this._config?.buttons?.secondaryButton) {
      this.attachDefaultButtonHandler(
        this._drawer.buttons.secondaryButton,
        'close'
      );
    }
  }

  /** Attach a close handler for a button click */
  private attachDefaultButtonHandler(
    button: DrawerButton | undefined,
    result: DrawerResultType
  ): void {
    if (button?.clicked$) {
      button.clicked$.subscribe(() => this.close(result));
    }
  }

  /** Handle cleanup and success when drawer closes */
  private handleDrawerClosed(drawerOutput: DrawerResult): void {
    this.enableBackgroundInteraction();
    this._trackedSubjects.forEach((s) => s.complete());
    this._trackedSubjects = [];

    if (drawerOutput.result === 'complete') {
      this.handleDrawerCompleteToast();
    }
  }

  /** Show completed toast if configured */
  private handleDrawerCompleteToast(): void {
    const { completedToastConfig } = this._config;
    if (completedToastConfig) {
      this.toastService.showToast(
        completedToastConfig.message,
        completedToastConfig.options ?? null
      );
    }
  }

  private disableBackgroundInteraction(): void {
    this._rootEl.setAttribute('inert', '');
    this._bodyEl.classList.add('tw-overflow-y-hidden');
  }

  private enableBackgroundInteraction(): void {
    this._rootEl.removeAttribute('inert');
    this._bodyEl.classList.remove('tw-overflow-y-hidden');
  }
}
